import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Save, ShieldCheck, Mail, CalendarClock, Send } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  getSecurityEmailConfig,
  saveSecurityEmailConfig,
  sendSecurityEmailNow,
} from "@/lib/api/security-email.functions";

export const Route = createFileRoute("/_authenticated/m/email-seguro")({
  component: Page,
});

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function Page() {
  const _get = useServerFn(getSecurityEmailConfig);
  const _save = useServerFn(saveSecurityEmailConfig);
  const _send = useServerFn(sendSecurityEmailNow);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState("");
  const [time, setTime] = useState("07:00");
  const [enabled, setEnabled] = useState(true);
  const [lastSent, setLastSent] = useState<string | null>(null);
  const [lastStatus, setLastStatus] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg: any = await _get();
        if (cfg) {
          setEmail(cfg.recipient_email ?? "");
          setTime(`${pad(cfg.send_hour ?? 7)}:${pad(cfg.send_minute ?? 0)}`);
          setEnabled(!!cfg.enabled);
          setLastSent(cfg.last_sent_at ?? null);
          setLastStatus(cfg.last_status ?? null);
        }
      } catch (e: any) {
        toast.error(e?.message || "Não foi possível carregar a configuração.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    const [h, m] = time.split(":");
    setSaving(true);
    try {
      await _save({
        data: {
          recipient_email: email.trim(),
          send_hour: Number(h ?? 7),
          send_minute: Number(m ?? 0),
          enabled,
        },
      });
      toast.success("Configuração salva.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r: any = await _send();
      toast.success(`Relatórios enviados para ${email}.`);
      setLastSent(new Date().toISOString());
      setLastStatus("enviado");
      void r;
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 text-primary p-2">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">E-mail Seguro</h1>
          <p className="text-sm text-muted-foreground">
            Automação e envio seguro de relatórios consolidados de alunos, contratos, caixa e
            comprovantes.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <Mail className="h-5 w-5" /> Configuração de Destinatário
          </CardTitle>
          <CardDescription>
            Defina o e-mail que receberá os relatórios automáticos e manuais.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="space-y-2">
              <Label htmlFor="sec-email">E-mail do Destinatário</Label>
              <Input
                id="sec-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@dominio.com"
              />
            </div>
            <Button onClick={save} disabled={saving || !email.trim()} className="sm:ml-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar e-mail
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" /> Agendamento Automático
            </CardTitle>
            <CardDescription>Configuração do envio diário.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Envio automático diário</p>
                <p className="text-xs text-muted-foreground">
                  Status: {enabled ? "Ativo" : "Inativo"}
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sec-hora">Horário diário (Brasília)</Label>
              <Input
                id="sec-hora"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              Os relatórios de <strong>Alunos</strong>, <strong>Responsáveis</strong>,{" "}
              <strong>Planos de Pagamento</strong>, <strong>Contratos</strong>,{" "}
              <strong>Reserva de Espaço</strong>, <strong>Caixa</strong>,{" "}
              <strong>Cobranças</strong> e <strong>Contas de Acesso</strong> (180 dias) são gerados e
              enviados automaticamente todos os dias.
              <p className="mt-2 text-xs text-muted-foreground">
                Capacidade: até 10.000 registros por relatório.
              </p>
            </div>

            <Button onClick={save} disabled={saving} variant="outline" className="w-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar agendamento
            </Button>
            {lastSent && (
              <p className="text-xs text-muted-foreground">
                Último envio: {new Date(lastSent).toLocaleString("pt-BR")}
                {lastStatus ? ` — ${lastStatus}` : ""}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" /> Envio Manual
            </CardTitle>
            <CardDescription>Gere e envie o relatório consolidado imediatamente.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={sendNow} disabled={sending || !email.trim()} className="w-full">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar Relatórios Agora
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Os arquivos CSV serão enviados para{" "}
              <strong className="text-foreground">{email || "—"}</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
